<template>
    <hts-drawer
        v-model="cVisible"
        size="90%"
        :title="drawerTitle"
        :loading="loading"
        @show="getJourney">
        <div class="p-3 flex gap-3 justify-start">
            <div class="w-full flex flex-col gap-2">
                <div
                    v-if="showCompanyInfo"
                    class="flex flex-col gap-2">
                    <p class="text-2xl text-main-600">
                        {{ $t('labels.customer') }}
                    </p>

                    <p class="font-bold">
                        {{ journey.customerData?.name }}
                    </p>
                    <p v-if="journey.customerData?.address">
                        Địa chỉ: {{ journey.customerData.address }}
                    </p>
                    <p v-if="journey.contactData?.name">
                        {{ $t('labels.businessContact') }}: {{ journey.contactData?.name }} - {{ journey.contactData?.phone }} - {{ journey.contactData?.email }}
                    </p>
                    <p v-if="journey.logisticContactData?.name">
                        {{ $t('labels.logisticContact') }}: {{ journey.logisticContactData?.name }} - {{ journey.logisticContactData?.phone }} -
                        {{ journey.logisticContactData?.email }}
                    </p>
                </div>
                <div class="flex flex-col gap-2">
                    <p class="text-2xl text-main-600">
                        {{ $t('labels.status') }}
                    </p>
                    <status
                        :status="journey.status"
                        module="journeyStatus"
                        key-status="status"
                        label="" />
                </div>
            </div>
            <div class="w-full">
                <p class="text-2xl text-main-600">
                    {{ $t('labels.route') }}
                </p>
                <ul
                    v-if="journey.stages && journey.stages.length"
                    class="steps steps-vertical w-full">
                    <template
                        v-for="(item, index) in (journey.stages as any[])"
                        :key="index">
                        <li
                            v-if="index >= journey.stageIndexBegin && index <= journey.stageIndexEnd"
                            data-content=" "
                            class="step float w-full">
                            <div class="flex items-center justify-center w-full">
                                <div
                                    class="items-center w-full text-left"
                                    :class="{
                                        'py-4': index !== 0
                                    }">
                                    <p class="font-bold">
                                        {{ item.fullNameNode }}
                                    </p>
                                    <p v-if="item.locationName">
                                        {{ item.locationName }}
                                    </p>
                                </div>
                            </div>
                        </li>
                    </template>
                </ul>
            </div>
            <div class="w-full">
                <p class="text-2xl text-main-600">
                    {{ $t('labels.otherInformation') }}
                </p>
                <p class="font-bold mt-2">
                    {{ $t('labels.commodity') }}
                </p>
                <p class="mb-2">
                    <span class="text-no first-letter:capitalize">
                        {{ dataOrder.commodityCategory }}
                    </span>
                    <span v-if="dataOrder.commodityCategory">
                        :
                    </span>
                    <span class="first-letter:capitalize">
                        {{ dataOrder.commodityName }}
                    </span>
                    <span v-if="dataOrder.goodsStatusName">
                        -
                    </span>
                    <span class="first-letter:capitalize">
                        {{ dataOrder.goodsStatusName }}
                    </span>
                </p>

                <template v-if="dataOrder.transportation === 'NON_CONTAINER'">
                    <p class="font-bold mt-2">
                        {{ $t('labels.vehicleType') }}
                    </p>
                    <p class="mb-2">
                        {{ dataOrder.vehicleCategoryName }}
                    </p>
                </template>
                <template v-else>
                    <p class="font-bold mt-2">
                        {{ $t('labels.containerType') }}
                    </p>
                    <p class="mb-2">
                        <span>
                            {{ dataOrder.contCategory }} feet
                        </span>
                        <span v-if="dataOrder.headContainer || dataOrder.mergeContainer">
                            - {{ dataOrder.headContainer ? `${$t('labels.headContainer')}` : '' }}{{ dataOrder.headContainer && dataOrder.mergeContainer ? ',' : ''
                            }}{{ dataOrder.mergeContainer ? `${$t('labels.mergeContainer')} ` : '' }}
                        </span>
                    </p>
                </template>

                <p class="font-bold mt-2">
                    {{ $t('labels.productivity') }}
                </p>
                <p class="mb-2">
                    {{ $formatters.formatNumber(dataOrder.productivityOrder || 0) }}
                    {{ dataOrder.transportation === 'NON_CONTAINER' ? $t('labels.vehicle') : $t('labels.container') }}
                </p>
            </div>
        </div>
        <div v-if="showTripAndOrderInfo">
            <div class="inline-block mt-1 px-2">
                <div class="font-bold">
                    {{ $t('labels.tripCode') }}
                </div>
                <div class="text-blue-400">
                    <router-link :to="tripRoute">
                        {{ journey.tripCode }}
                    </router-link>
                </div>
            </div>
            <div class="inline-block mt-1 px-2">
                <div class="font-bold">
                    {{ $t('labels.orderCode') }}
                </div>
                <div class="text-blue-400">
                    <router-link :to="orderRoute">
                        {{ journey.orderCode }}
                    </router-link>
                </div>
            </div>
            <div
                v-if="beforeJourney?.code && beforeJourneyRoute"
                class="inline-block mt-1 px-2">
                <div class="font-bold">
                    {{ $t('labels.beforeJourney') }}
                </div>
                <div class="text-blue-400">
                    <router-link :to="beforeJourneyRoute">
                        {{ beforeJourney.code }}
                    </router-link>
                </div>
            </div>
            <div
                v-if="nextJourney?.code && nextJourneyRoute"
                class="inline-block mt-1 px-2">
                <div class="font-bold">
                    {{ $t('labels.nextJourney') }}
                </div>
                <div class="text-blue-400">
                    <router-link :to="nextJourneyRoute">
                        {{ nextJourney.code }}
                    </router-link>
                </div>
            </div>
        </div>

        <div
            v-if="!loading"
            class="mx-5 mb-20">
            <hts-ui-tabs
                v-model="tapSelected"
                :tabs="tabs"
                :exclude="exclude">
                <template #default="{ key, label }: any">
                    <div class="relative">
                        {{ label }}
                        <template v-if="key === 'driver-notifications' && hasDriverAlert">
                            <span class="absolute text-sm -top-2 -right-6 text-white bg-error-500 font-medium border px-2 rounded-full">
                                {{ hasDriverAlert }}
                            </span>
                        </template>
                        <template v-if="key === 'alerts' && hasAlert">
                            <span class="absolute text-sm -top-2 -right-6 text-white bg-error-500 font-medium border px-2 rounded-full">
                                {{ hasAlert }}
                            </span>
                        </template>
                    </div>
                </template>
            </hts-ui-tabs>
            <div
                v-show="tapSelected === 'monitor'"
                class="py-1">
                <travel-detail
                    v-if="journey.travelDetails"
                    ref="travelDetail"
                    :journey="journey"
                    :editable="editable"
                    @journey-changed="handleChange" />
            </div>

            <div v-show="tapSelected === 'prices'">
                <info-prices
                    :journey="journey"
                    @modified="handleChange" />
            </div>
        </div>
    </hts-drawer>
</template>
<script lang="ts" setup>
import InfoPrices from './journey-info/prices.vue'
import TravelDetail from './journey-info/travel-detail.vue'
import { getTimeConditions } from './time-condition-format'
import Status from '~/components/status.vue'
import { checkPermission } from '@/utils/permission-check'

const { $event, $formatters, $eventBus } = useNuxtApp()
const props = defineProps({
  api: {
    type: Object,
    required: true
  },
  exclude: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  travelExclude: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  editable: {
    type: Boolean,
    default: true
  }
})

const { api } = toRefs(props)

const loading = ref(true)
const cVisible = ref(false)
const monitoringHeight = ref(0)
const monitoring = ref()
const tapSelected = ref('monitor')

const tabs = ref([
  { key: 'monitor', label: $t('labels.monitoring'), permission: true },
  { key: 'prices', label: $t('labels.prices'), permission: checkPermission(['monitoring_journey_pricing_view']) }
])
const drawerTitle = computed(() => `Thông tin chặng #${journey.code}`)
const journey = reactive<any>({
  _id: '',
  code: '',
  stages: [],
  stageNames: [],
  timeIn: null,
  timeOut: null,
  vehicleId: null,
  vehicleTempId: null,
  order: {},
  alerts: [],
  stageIndexStart: 0,
  stageIndexEnd: 0,
  trip: null
})

const hasVehicle = computed(() => !!(journey.vehicleId || journey.vehicleTempId))
const hasValidTime = computed(() => !!(journey.timeIn && journey.timeOut))
const journeyId = ref<string | null>(null)
const dataOrder = computed<any>(() => journey?.order || {})
const alerts = computed(() => (journey.alerts || []).filter((a: any) => !a.skipped && !a.resolved))
const hasAlert = computed(() => Array.isArray(alerts.value) && alerts.value.length)
const driverAlerts = computed(() => (journey.driverAlerts || []).filter((a: any) => !a.skipped && !a.resolved))
const hasDriverAlert = computed(() => Array.isArray(driverAlerts.value) && driverAlerts.value.length)
const tabFile = ref<any>(null)
const tabActivity = ref<any>(null)
const tabAlert = ref<any>(null)
const tabDriverNotification = ref<any>(null)
const tabPlanning = ref<any>(null)
const tabPlanningActual = ref<any>(null)
const travelDetail = ref<any>(null)
const nextJourney = computed<any>(() => journey.afterJourney)
const beforeJourney = computed<any>(() => journey.beforeJourney)
const store = $store()
const companyType = computed(() => store.auth.companyType)
const companyTypeOwnerPage = 'OWNER'

const showCompanyInfo = computed(() => {
  return companyType.value === companyTypeOwnerPage
})
const showTripAndOrderInfo = computed(() => {
  return companyType.value === companyTypeOwnerPage
})

onMounted(() => {
  $eventBus.on('journey-change', handleJourneyChange)
})

onUnmounted(() => {
  $eventBus.off('journey-change', handleJourneyChange)
})

const handleJourneyChange = $lodash.debounce((info: any) => {
  Object.assign(journey, info)
  refresh()
}, 3000)

const getJourney = (silence: boolean = false) => {
  if (!silence) {
    loading.value = true
  }
  $api(api.value, { uriParams: { ID: journeyId.value } }).then(({ data }: any) => {
    const temp = (data?.value || {}).data || {}

    Object.assign(journey, temp)

    if (!temp.customerData) {
      journey.customerData = {}
    }
    if (!temp.contactData) {
      journey.contactData = {}
    }
    if (!temp.logisticContactData) {
      journey.logisticContactData = {}
    }

    journey.travelDetails = journey.travelDetails.map((t: any, i: number) => {
      if (t.type === 'task') {
        t.timingStartArr = getTimeConditions($formatters, t, 'start')
        t.timingEndArr = getTimeConditions($formatters, t, 'end')
      }
      Object.assign(t, {
        travelIndex: i,
        ata: $formatters.datetime(t.ata, 'YYYY-MM-DD HH:mm'),
        atd: $formatters.datetime(t.atd, 'YYYY-MM-DD HH:mm')
      })
      return t
    })
    loading.value = false
    nextTick(() => {
      showTravelDetails()
    })
  })
}

const showTravelDetails = () => {
  if (!travelDetail.value) {
    setTimeout(() => {
      showTravelDetails()
    }, 1000)
    return
  }
  travelDetail.value.show(journey)
}

const show = async (id: string) => {
  if (cVisible.value) {
    cVisible.value = false
    await new Promise((resolve: any) => {
      setTimeout(() => resolve(), 300)
    })
  }
  Object.assign(journey, {
    _id: '',
    code: '',
    stages: [],
    stageNames: [],
    timeIn: null,
    timeOut: null,
    vehicleId: null,
    vehicleTempId: null,
    order: {}
  })
  journeyId.value = id
  cVisible.value = true
  setJourneyRead()
}

const hide = () => {
  cVisible.value = false
}
const refresh = (id?: string) => {
  if (!cVisible.value) {
    return
  }
  if (id && journeyId.value !== id) {
    return
  }

  getJourney(true)
  tabAlert.value && tabAlert.value.refresh()
  tabDriverNotification.value && tabDriverNotification.value.refresh()
  tabFile.value && tabFile.value.refresh()
  tabActivity.value && tabActivity.value.refresh()
  tabPlanning.value && tabPlanning.value.refresh()
  tabPlanningActual.value && tabPlanningActual.value.refresh()
}

const setJourneyRead = () => {
  if (!journeyId.value) {
    return
  }
  $api($url.monitoring.journey.read, { uriParams: { ID: journeyId.value } })
}

const handleChange = $lodash.debounce(() => {
  refresh()
}, 300)

defineExpose({ show, hide, refresh })
const company = computed(() => $store().system.getCompany)

watch(
  () => tapSelected.value,
  (val) => {
    if (val === 'monitoring') {
      setMonitoringHeight()
    }
  }
)

const setMonitoringHeight = () => {
  nextTick(() => {
    // console.log('monitoring')
    monitoringHeight.value = monitoring.value.clientHeight
  })
}

const tripRoute = computed(() => {
  return {
    name: 'trucker-id-trip',
    query: { id: journey.trip }
  }
})
const orderRoute = computed(() => {
  return {
    name: 'trucker-id-order',
    query: { id: journey.order?._id }
  }
})
const nextJourneyRoute = computed(() => {
  if (!nextJourney.value) {
    return null
  }
  return {
    name: nextJourney.value?.partnerId ? 'trucker-id-monitoring-partner' : 'trucker-id-monitoring-owner',
    query: { journeyId: nextJourney.value?._id }
  }
})
const beforeJourneyRoute = computed(() => {
  if (!beforeJourney.value) {
    return null
  }
  return {
    name: beforeJourney.value?.partnerId ? 'trucker-id-monitoring-partner' : 'trucker-id-monitoring-owner',
    query: { journeyId: beforeJourney.value?._id }
  }
})
</script>
